import Vue from 'vue';
import Api from "./services/Api";
import keyBy from 'lodash.keyby';

const state = Vue.observable({
    newContactId: null,
    user: null,
    socketId: null,
    discussions: {},
    messages: {},
    formationsList: {},
    campuses: {},
    suggestions: {},
    alerts: {},
    folders: {},
    openedDiscussionId: null
});

export const getters = {
    newContactId: () => state.newContactId,
    campuses: () => Object.values(state.campuses),
    socketId: () => state.socketId,
    folders: () => Object.values(state.folders),
    alerts: () => Object.values(state.alerts).sort((a, b) => b.created_at - a.created_at),
    suggestion: (id) => state.suggestions[id],
    suggestions: () => Object.values(state.suggestions),
    formationsList: () => Object.values(state.formationsList),
    user: () => state.user,
    discussionMessages: (id) => {
        return Object.values(state.messages)
            .filter(message => message.discussion_id === id)
            .sort((a, b) => a.created_at - b.created_at)
    },
    discussionName: (id) => {
        return getters.discussion(id).users.filter(user => user.id !== getters.user().id).map(user => user.profile.name).join(', ');
    },
    discussions: () => Object.keys(state.discussions).map(getters.discussion).sort((a, b) => b.updated_at - a.updated_at),
    discussion: (id) => {
        if (!state.discussions[id]) {
            return null;
        }

        let messages = getters.discussionMessages(id);
        return {
            ...state.discussions[id],
            last_message: messages[messages.length - 1]
        };
    },
    discussionWithUser: (userId) => getters.discussions().find(discussion => !!discussion.users.find(user => user.id === userId)),
    openedDiscussionId: () => state.openedDiscussionId,
    openedDiscussion: () => state.openedDiscussionId ? getters.discussion(state.openedDiscussionId) : null
};

export const mutations = {
    clearNewContactId: () => state.newContactId = null,
    setNewContactId: (newContactId) => state.newContactId = newContactId,
    clearSocketId: () => state.socketId = null,
    setSocketId: (socketId) => state.socketId = socketId,
    setUser: (user) => state.user = user,
    pushDiscussions: (discussions) => {
        if (!(discussions instanceof Array)) {
            discussions = [discussions];
        }

        discussions = discussions.map(discussion => {
            if (discussion.last_message?.id) {
                mutations.pushMessages(discussion.last_message);
                discussion.last_message = discussion.last_message.id;
            }

            return discussion;
        });

        state.discussions = {
            ...state.discussions,
            ...keyBy(discussions, 'id')
        }
    },
    removeFolder: (id) => {
        let newFolders = {...state.folders};
        delete newFolders[id];
        state.folders = newFolders;
    },
    removeFormationFolder: (folderId, formationId) => {
        let folder = {
            ...state.folders[folderId],
            formations: state.folders[folderId].formations.filter(formation => formation.id !== formationId)
        };
        state.folders = {
            ...state.folders,
            [folderId]: folder
        };
    },
    pushFormationsList: (formations) => {
        if (!(formations instanceof Array)) {
            formations = [formations];
        }

        state.formationsList = {
            ...state.formationsList,
            ...keyBy(formations, 'id')
        };
    },
    pushFolders: (folders) => {
        if (!(folders instanceof Array)) {
            folders = [folders];
        }

        state.folders = {
            ...state.folders,
            ...keyBy(folders, 'id')
        };
    },
    pushAlerts: (alerts) => {
        if (!(alerts instanceof Array)) {
            alerts = [alerts];
        }

        state.alerts = {
            ...state.alerts,
            ...keyBy(alerts, 'id')
        };
    },
    setSuggestions: (suggestions) => {
        if (!(suggestions instanceof Array)) {
            suggestions = [suggestions];
        }

        state.suggestions = keyBy(suggestions, 'id');
    },
    setCampuses: (campuses) => {
        if (!(campuses instanceof Array)) {
            campuses = [campuses];
        }

        state.campuses = keyBy(campuses, 'id');
    },
    pushMessages: (messages) => {
        if (!(messages instanceof Array)) {
            messages = [messages];
        }

        state.messages = {
            ...state.messages,
            ...keyBy(messages, 'id')
        }
    },
    setOpenedDiscussionId: id => state.openedDiscussionId = id,
    closeDiscussion: () => state.openedDiscussionId = null
};

export const actions = {
    async fetchCampuses() {
        const res = await Api.get(`/campuses`);
        mutations.setCampuses(res.data);
        return res;
    },
    async fetchSuggestions(folderId) {
        mutations.setSuggestions([]);
        const res = await Api.get(`/folders/${folderId}/suggestions`);
        mutations.setSuggestions(res.data);
        return res;
    },
    async fetchAlerts() {
        const res = await Api.get(`/account/alerts`);
        mutations.pushAlerts(res.data);
        return res;
    },
    async fetchDiscussions(params = {}) {
        const res = await Api.get('/discussions', params);
        mutations.pushDiscussions(res.data);
        return res;
    },
    async fetchFolder() {
        const res = await Api.get('/folders');
        mutations.pushFolders(res.data);
        return res;
    },
    async fetchFormationsList() {
        const res = await Api.get('/formations?mode=list');
        mutations.pushFormationsList(res.data);
        return res;
    },
    async fetchMessages(discussionId, params = {}) {
        const res = await Api.get(`/discussions/${discussionId}/messages`, params);
        mutations.pushMessages(res.data);
        return res;
    },
    async markAsRead(discussionId) {
        const res = await Api.post(`/discussions/${discussionId}/mark-as-read`);
        await mutations.pushDiscussions(res.data);
        return res;
    },
    async fetchUser() {
        const res = await Api.get('/account/me');
        mutations.setUser(res.data);
        return res;
    },
    async updateProfile(data) {
        const res = await Api.put('/account/profile', data);
        mutations.setUser(res.data);
        return res;
    },
    async createProfile(data) {
        const res = await Api.post('/account/profile', data);
        mutations.setUser(res.data);
        return res;
    },
    async updateEmail(email) {
        const res = await Api.put('/account/email', {email});
        mutations.setUser(res.data);
        return res;
    }
};
